import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, AlertTriangle, Check, Info, Download, ChevronDown, Crop, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import ImageCropperModal from './ImageCropperModal';
import { validateImage, IMAGE_SPECS, formatFileSize } from '../utils/imageValidation';

const API = '/api';

// Placeholder component when no cover exists
const CoverPlaceholder = ({ contentTitle, creatorName }) => (
  <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex flex-col items-center justify-center text-center p-4">
    <ImageIcon className="w-12 h-12 text-zinc-600 mb-3" />
    <p className="text-zinc-400 font-medium text-sm line-clamp-2">{contentTitle || 'Untitled'}</p>
    {creatorName && <p className="text-zinc-500 text-xs mt-1">by {creatorName}</p>}
  </div>
);

const CoverImageUploader = ({ 
  contentType, // 'series' or 'novel'
  contentId,
  currentCoverUrl,
  onCoverUpdate,
  lastUpdated,
  creatorName,
  contentTitle
}) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(currentCoverUrl);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [validationWarning, setValidationWarning] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const spec = IMAGE_SPECS.COVER;

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset states
    setValidationError(null);
    setValidationWarning(null);
    setPendingUrl(null);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // Use the new forgiving validation
    const validation = await validateImage(file, 'COVER');
    
    if (!validation.canUpload) {
      // Only hard errors block upload
      setValidationError(validation.friendlyMessage || validation.error);
      return;
    }
    
    // Show warning if there is one (but don't block)
    if (validation.warning) {
      setValidationWarning(validation.friendlyMessage || validation.warning);
    }

    // If needs cropping, open the cropper
    if (validation.needsCrop) {
      setFileToProcess(file);
      setShowCropper(true);
      return;
    }

    // Image is good - proceed with upload directly
    await uploadFile(file);
  };

  // Handle cropped file from the modal
  const handleCroppedFile = async (croppedFile) => {
    setShowCropper(false);
    setFileToProcess(null);
    setValidationWarning(null); // Clear warning after crop
    await uploadFile(croppedFile);
  };

  // Skip cropping and upload directly (auto-adjust on server)
  const handleSkipCrop = async () => {
    if (fileToProcess) {
      setShowCropper(false);
      await uploadFile(fileToProcess);
      setFileToProcess(null);
    }
  };

  // Upload the file (either original or cropped)
  const uploadFile = async (file) => {
    // Show preview immediately
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('content_type', contentType);

      const response = await axios.post(`${API}/upload/cover-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.url) {
        setPendingUrl(response.data.url);
        setPreview(response.data.url);
        toast.success('Cover uploaded! Click "Save Cover" to apply.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail || 'Failed to upload cover';
      toast.error(detail);
      setValidationError(detail);
      setPreview(currentCoverUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!pendingUrl) return;

    setSaving(true);
    try {
      const endpoint = contentType === 'series' 
        ? `${API}/series/${contentId}/cover`
        : `${API}/novels/${contentId}/cover`;

      const response = await axios.put(endpoint, 
        { cover_url: pendingUrl },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Cover image saved successfully!');
        setPendingUrl(null);
        setValidationWarning(null);
        if (onCoverUpdate) {
          onCoverUpdate(pendingUrl);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save cover');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the cover image? A placeholder will be shown instead.')) return;

    setSaving(true);
    try {
      const endpoint = contentType === 'series' 
        ? `${API}/series/${contentId}/cover`
        : `${API}/novels/${contentId}/cover`;

      const response = await axios.put(endpoint, 
        { cover_url: null },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Cover image removed');
        setPreview(null);
        setPendingUrl(null);
        setValidationWarning(null);
        if (onCoverUpdate) {
          onCoverUpdate(null);
        }
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove cover');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPreview(currentCoverUrl);
    setPendingUrl(null);
    setValidationError(null);
    setValidationWarning(null);
  };

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-800" data-testid="cover-image-uploader">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-400" />
                Cover Image
              </CardTitle>
              <CardDescription className="mt-1">
                Portrait cover for cards, search results, and featured sections.
              </CardDescription>
            </div>
            {currentCoverUrl && !pendingUrl && (
              <Badge variant="secondary" className="bg-green-500/20 text-green-400">
                <Check className="w-3 h-3 mr-1" />
                Cover Set
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Friendly Guidelines */}
          <div className="flex items-start gap-2 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-300">
              <p className="font-medium mb-1">Upload any portrait image — we'll make it fit!</p>
              <ul className="text-purple-400 space-y-0.5 text-xs">
                <li>• Accepts most portrait images (tall shapes work best)</li>
                <li>• We'll auto-adjust images that need minor cropping</li>
                <li>• Recommended: {spec.recWidth}×{spec.recHeight}px • Max: {formatFileSize(spec.maxFileSize)}</li>
              </ul>
            </div>
          </div>

          {/* Template Download */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                data-testid="download-cover-template-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Download Template
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
              </Button>
              
              {showTemplateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                  <a
                    href="/templates/cover-template-1600x2400.png"
                    download="cover-template-1600x2400.png"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                    onClick={() => setShowTemplateDropdown(false)}
                  >
                    <ImageIcon className="w-4 h-4 text-purple-400" />
                    <div>
                      <div className="font-medium">1600×2400</div>
                      <div className="text-xs text-zinc-500">Recommended</div>
                    </div>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Soft Warning (non-blocking) */}
          {validationWarning && !validationError && (
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="cover-validation-warning">
              <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-300">{validationWarning}</div>
            </div>
          )}

          {/* Hard Error (blocking) */}
          {validationError && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="cover-validation-error">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">{validationError}</div>
            </div>
          )}

          {/* Preview / Upload Area - 2:3 aspect ratio frame */}
          <div className="space-y-3">
            {preview ? (
              <div className="relative group flex justify-center">
                <div className="relative w-48 bg-zinc-800 rounded-lg overflow-hidden shadow-lg" style={{ aspectRatio: '2/3' }}>
                  <img
                    src={preview}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    data-testid="cover-preview-image"
                  />
                  
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleFileSelect}
                        className="sr-only"
                        disabled={uploading || saving}
                      />
                      <Button size="sm" variant="secondary" asChild disabled={uploading}>
                        <span>
                          <Upload className="w-4 h-4 mr-1" />
                          Replace
                        </span>
                      </Button>
                    </label>
                    {currentCoverUrl && !pendingUrl && (
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={handleRemove}
                        disabled={saving}
                        data-testid="remove-cover-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  
                  {pendingUrl && (
                    <div className="absolute top-2 right-2">
                      <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400 text-xs">
                        Unsaved
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileSelect}
                  className="sr-only"
                  disabled={uploading}
                  data-testid="cover-file-input"
                />
                <div className="flex justify-center">
                  <div 
                    className={`
                      relative w-48 border-2 border-dashed border-zinc-600 rounded-lg
                      hover:border-purple-500 transition-colors
                      flex flex-col items-center justify-center
                      ${uploading ? 'opacity-50' : ''}
                    `}
                    style={{ aspectRatio: '2/3' }}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                        <span className="text-zinc-400 text-sm mt-2">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <CoverPlaceholder contentTitle={contentTitle} creatorName={creatorName} />
                        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-lg">
                          <Upload className="w-8 h-8 text-white mb-2" />
                          <p className="text-white text-sm font-medium">Upload Cover</p>
                          <p className="text-zinc-300 text-xs">Any portrait image works!</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </label>
            )}
          </div>

          {/* Action Buttons */}
          {pendingUrl && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="save-cover-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Cover
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
                className="border-zinc-700"
                data-testid="cancel-cover-btn"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Last Updated */}
          {lastUpdated && !pendingUrl && (
            <p className="text-xs text-zinc-500 text-center">
              Last updated: {new Date(lastUpdated).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Image Cropper Modal */}
      <ImageCropperModal
        open={showCropper}
        onClose={() => {
          setShowCropper(false);
          setFileToProcess(null);
        }}
        file={fileToProcess}
        aspect={spec.targetAspect}
        minWidth={spec.minWidth}
        minHeight={spec.minHeight}
        outputWidth={spec.recWidth}
        outputHeight={spec.recHeight}
        maxBytes={spec.maxFileSize}
        imageType="Cover Image"
        onCroppedFileReady={handleCroppedFile}
      />
    </>
  );
};

export default CoverImageUploader;
